import React from "react";
import { Badge, CopyBtn, TxDetailRow } from "@/components/primitives";
import { AdaAmount } from "@/components/tx/AdaAmount";
import { useChainTip } from "@/hooks";
import { formatLovelace, truncMid, unixToDate } from "@/lib/format";

const computeAssurance = (confirmations) => {
  if (confirmations == null) return { label: "Pending", tone: "warn" };
  if (confirmations >= 12) return { label: "High", tone: "success" };
  if (confirmations >= 4) return { label: "Medium", tone: "info" };
  return { label: "Low", tone: "warn" };
};

// ---------- Cardanoscan-style transaction summary tab ----------
export const TransactionSummaryPanel = ({ data, amountMode, onPickBlock }) => {
  const { tip } = useChainTip(20000);
  if (!data) return null;
  const lovelace = data.output_amount?.find?.((a) => a.unit === "lovelace")?.quantity || "0";
  const confirmations =
    data.block_height != null && tip?.height != null
      ? Math.max(0, tip.height - data.block_height)
      : null;
  const assurance = computeAssurance(confirmations);
  const confirmedWithin = data.block_time
    ? `Confirmed within ${Math.max(1, Math.floor((Date.now() / 1000 - data.block_time)))} secs`
    : null;
  const feesLov = BigInt(data.fees || 0);
  const depositLov = BigInt(data.deposit || 0);
  const outputLov = BigInt(lovelace || 0);
  const totalInputLov = outputLov + feesLov + depositLov;
  const denom = outputLov + feesLov;
  const feesPct = denom > 0n ? Number((feesLov * 1000000n) / denom) / 10000 : 0;
  const DONUT_C = 2 * Math.PI * 54;
  return (
    <div className="space-y-4" data-testid="transaction-summary-panel">
      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="rounded-2xl border border-primary/30 bg-primary/[0.06] px-5 py-4" data-testid="tx-stat-input">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Total Input</div>
          <AdaAmount lovelaceStr={totalInputLov.toString()} />
        </div>
        <div className="rounded-2xl border border-primary/30 bg-primary/[0.06] px-5 py-4" data-testid="tx-stat-output">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Total Output</div>
          <AdaAmount lovelaceStr={outputLov.toString()} />
        </div>
        <div className="rounded-2xl border border-orange-500/30 bg-orange-500/[0.06] px-5 py-4" data-testid="tx-stat-fees">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Total Fees</div>
          <div className="font-heading text-[18px] md:text-[20px] tabnum text-orange-400">{formatLovelace(data.fees, amountMode)}</div>
        </div>
        <div className="rounded-2xl border border-orange-500/30 bg-orange-500/[0.06] px-5 py-4" data-testid="tx-stat-net">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Net Change</div>
          <div className="font-heading text-[18px] md:text-[20px] tabnum text-orange-400">-{formatLovelace((feesLov + depositLov).toString(), amountMode)}</div>
        </div>
      </div>

      {/* Two panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
        {/* Transaction Info */}
        <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow px-6 py-4">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
          <h3 className="font-heading text-[15px] md:text-[16px] text-[#22d3ee] text-glow-cyan mb-2">Transaction Info</h3>
          <TxDetailRow label="Status" testid="tx-row-status">
            <span className="inline-flex items-center gap-2">
              <Badge tone="success">Confirmed</Badge>
              {confirmations != null ? (
                <span className="text-muted-foreground/80 normal-case">
                  ({confirmations === 0 ? "in current block" : confirmedWithin})
                </span>
              ) : null}
            </span>
          </TxDetailRow>
          <TxDetailRow label="Block" testid="tx-row-block">
            {data.block_height != null ? (
              <button
                type="button"
                data-testid="tx-row-block-link"
                onClick={() => onPickBlock?.(data.block_height)}
                className="text-primary text-glow hover:underline decoration-dotted underline-offset-2 transition-colors"
              >
                #{data.block_height.toLocaleString("en-US")}
              </button>
            ) : "—"}
            {data.block ? (
              <span className="text-muted-foreground/80 ml-2 inline-flex items-center gap-1">
                · {truncMid(data.block, 10, 6)}
                <CopyBtn value={data.block} label="block-hash" />
              </span>
            ) : null}
          </TxDetailRow>
          <TxDetailRow label="Timestamp" testid="tx-row-timestamp">
            <span className="text-foreground">{data.block_time ? unixToDate(data.block_time) : "—"}</span>
          </TxDetailRow>
          <TxDetailRow label="Epoch / Slot" testid="tx-row-epoch-slot">
            <span className="text-foreground">
              {data.epoch ?? "—"} / {data.epoch_slot != null ? data.epoch_slot.toLocaleString("en-US") : (data.slot != null ? data.slot.toLocaleString("en-US") : "—")}
            </span>
          </TxDetailRow>
          <TxDetailRow label="Absolute Slot" testid="tx-row-abs-slot">
            <span className="text-foreground">{data.slot != null ? data.slot.toLocaleString("en-US") : "—"}</span>
          </TxDetailRow>
          <TxDetailRow label="Assurance" testid="tx-row-assurance">
            <span className="inline-flex items-center gap-2">
              <Badge tone={assurance.tone}>{assurance.label}</Badge>
              {confirmations != null ? (
                <span className="text-muted-foreground/80 normal-case">
                  {confirmations} confirmation{confirmations === 1 ? "" : "s"}
                </span>
              ) : null}
            </span>
          </TxDetailRow>
          <TxDetailRow label="Certificates" testid="tx-row-certs">
            <span className="text-foreground">{data.stake_cert_count ?? 0}</span>
          </TxDetailRow>
          <TxDetailRow label="Tx Hash" testid="tx-row-hash">
            <span className="inline-flex items-center gap-2 justify-end">
              <span className="text-primary text-glow break-all">{truncMid(data.hash, 16, 6)}</span>
              <CopyBtn value={data.hash} label="tx-hash" />
            </span>
          </TxDetailRow>
        </div>

        {/* Quick View */}
        <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md px-6 py-4" data-testid="tx-quick-view">
          <h3 className="font-heading text-[15px] md:text-[16px] text-[#22d3ee] text-glow-cyan mb-4">Quick View</h3>
          <div className="flex flex-col items-center">
            <div className="relative w-[200px] h-[200px]">
              <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                <circle cx="70" cy="70" r="54" fill="none" stroke="hsl(264 100% 68%)" strokeWidth="12" />
                <circle
                  cx="70" cy="70" r="54" fill="none" stroke="#fb923c" strokeWidth="12" strokeLinecap="round"
                  strokeDasharray={`${(feesPct / 100) * DONUT_C} ${DONUT_C - (feesPct / 100) * DONUT_C}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Fees</span>
                <span className="font-heading text-[14px] tabnum text-foreground mt-1">{formatLovelace(data.fees, amountMode)}</span>
                <span className="text-[12px] tabnum text-orange-400 mt-0.5">{feesPct.toFixed(2)}%</span>
              </div>
            </div>
            <div className="w-full mt-6 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground"><span className="w-3 h-3 rounded-full bg-primary" /> Amount Sent</span>
                <span className="mono tabnum text-foreground text-[13px]">{formatLovelace(lovelace, amountMode)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground"><span className="w-3 h-3 rounded-full bg-orange-400" /> Fees</span>
                <span className="mono tabnum text-orange-400 text-[13px]">{formatLovelace(data.fees, amountMode)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
